import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";

const PRODUCT_CARDS = [
  {
    title: "Sales Centers",
    img: "/images/ext-sales-hardilap.jpg",
    text: "Make a powerful first impression with a professional modular sales center — from Hardi lap with full shingle mansards to custom branded exteriors.",
  },
  {
    title: "Office Buildings",
    img: "/images/ext-office-hardi.jpg",
    text: "Executive offices and multi-room office buildings, ground set to match existing architecture or built stand-alone to your specification.",
  },
  {
    title: "Educational Facilities",
    img: "/images/ext-education.jpg",
    text: "Classrooms, daycares, and libraries engineered to meet or surpass all applicable building code requirements — delivered fast for growing schools.",
  },
  {
    title: "Health Care / Medical",
    img: "/images/int-imaging.jpg",
    text: "Medical offices, imaging rooms, and clinical facilities constructed with the same or higher quality materials as any site-built building.",
  },
  {
    title: "Commercial Applications",
    img: "/images/ext-auto-sales.jpg",
    text: "From auto sales centers to club houses, pro shops, and shower/laundry facilities — modular solutions for every commercial space need.",
  },
];

const Products = () => {
  return (
    <div data-testid="products-page">
      <section className="relative overflow-hidden bg-[#001529]">
        <img src="/images/ext-proshop-stucco.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">VBS Products</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              Solutions for any space need
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              From basic offices to complex multi-story buildings, VBS provides solutions for any of your space needs.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {PRODUCT_CARDS.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 90}>
              <article
                data-testid={`product-card-${p.title.toLowerCase().replace(/[\s/]+/g, "-")}`}
                className="group h-full overflow-hidden rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 sm:p-7 flex flex-col flex-1">
                  <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900">{p.title}</h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.text}</p>
                  <Link
                    to="/contact"
                    data-testid={`product-quote-${p.title.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#00203d] hover:text-amber-600 transition-colors duration-200"
                  >
                    Request a quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}

          <Reveal delay={180}>
            <div className="h-full rounded-xl bg-[#00203d] blueprint-grid p-6 sm:p-8 flex flex-col justify-center">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Every project includes</h2>
              <ul className="mt-5 space-y-3">
                {[
                  "Custom design to your space requirements",
                  "Code-compliant engineering (wood, steel or concrete)",
                  "Expandable & relocatable construction",
                  "Turn-key delivery and installation",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" aria-hidden="true" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/gallery"
                data-testid="products-gallery-link"
                className="mt-7 inline-flex items-center gap-2 font-bold text-amber-500 hover:text-amber-400 transition-colors duration-200"
              >
                See real examples <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Products;
